import { DateTime } from 'luxon'
import { OptimizerSchedule, RelayReason } from '../../types/ports'

export interface PlanSegment {
  start: number
  end: number
  // 0..100 %: the optimizer power level 0..7 as the server decodes it (0, 15, 30, 45, 60, 70, 85, 100).
  level: number
  reason: RelayReason
  // No plan: the heat pump runs on its own control (REASON_DEFAULT on a heat pump channel).
  released: boolean
}

const QUARTER_MS = 15 * 60 * 1000
const MIN_WINDOW_MS = 3 * 60 * 60 * 1000

/**
 * On a heat pump channel REASON_DEFAULT (sent with power 7) means "no plan,
 * the machine is released to its own weather curve", not "default ON".
 */
export function isReleased(reason: RelayReason): boolean {
  return reason === 'REASON_DEFAULT'
}

/**
 * Turn an optimizer plan into level segments from `now` (ms) on. An event
 * lasts until the next one; the last one is drawn for one quarter, since the
 * plan does not say when it ends. REASON_END_OF_PREVIOUS_ITEM frees the
 * control and is not drawn. The window is at least three hours long.
 */
export function planSegments(schedule: OptimizerSchedule, now: number): { from: number, to: number, segments: PlanSegment[] } {
  const events = schedule
    .map(([time, command]) => ({ ts: DateTime.fromISO(time).toMillis(), command }))
    .filter(e => Number.isFinite(e.ts))
    .sort((a, b) => a.ts - b.ts)

  const segments: PlanSegment[] = []
  events.forEach((event, idx) => {
    const next = idx + 1 < events.length ? events[idx + 1].ts : event.ts + QUARTER_MS
    const start = Math.max(event.ts, now)
    if (next <= start || event.command.reason === 'REASON_END_OF_PREVIOUS_ITEM') return
    const value = event.command.value as unknown
    const level = value === true ? 100 : (typeof value === 'number' ? value : 0)
    segments.push({ start, end: next, level, reason: event.command.reason, released: isReleased(event.command.reason) })
  })

  const lastEnd = segments.length ? segments[segments.length - 1].end : now
  return { from: now, to: Math.max(lastEnd, now + MIN_WINDOW_MS), segments }
}

/**
 * The planned level (0..100 %) in effect at `now`; null when the plan says
 * nothing or the heat pump is released to its own control.
 */
export function currentPlanLevel(schedule: OptimizerSchedule, now: number): number | null {
  const { segments } = planSegments(schedule, now)
  const seg = segments.find(s => s.start <= now && now < s.end)
  return seg && !seg.released ? seg.level : null
}
